import classNames from 'classnames'
import { BreakoutTooltip } from 'components/design-system/BreakoutTooltip'
import { ChevronRight } from 'components/icons/ChevronRight'
import type { RouteType } from 'config/routes'
import { AnimatePresence, m } from 'framer-motion'
import { useRootStore } from 'hooks/rootStore'
import { useTruncationDetection } from 'hooks/useTruncationDetection'
import { reaction } from 'mobx'
import type { QueryParams } from 'mobx-router'
import type { DOMAttributes } from 'react'
import React, { Children, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslationTyped } from 'i18n/i18n'

export function MainPane({
  children,
  className,
  testId,
}: {
  children: React.ReactNode
  className?: string
  testId?: string
}) {
  return (
    <div
      data-testid={testId}
      className={classNames(
        'box-border flex-grow rounded-3xl bg-core-tertiary p-5 pb-8 md:h-full md:p-10',
        className
      )}
    >
      <div className="items-left flex h-full flex-col align-middle">
        {children}
      </div>
    </div>
  )
}

export function Header(
  props: React.PropsWithChildren<DOMAttributes<HTMLDivElement>> & {
    className?: string
  }
) {
  return (
    <div
      className="flex flex-grow-0 flex-row items-center justify-between whitespace-nowrap"
      {...props}
    >
      {props.children}
    </div>
  )
}

export type HeadlineBreadcrumb = {
  label: string
  onClick?: () => void
}

function BreadcrumbButton({
  breadcrumb,
  breadcrumbCount,
  isLast,
}: {
  breadcrumb: HeadlineBreadcrumb
  breadcrumbCount: number
  tooltip?: string
  label?: string
  isLast: boolean
}) {
  const ref = useRef<HTMLAnchorElement | null>(null)

  return (
    <a
      ref={ref}
      title={breadcrumb.label}
      href={isLast ? '' : '#'}
      aria-current={isLast ? 'page' : undefined}
      className={classNames(
        'focus-ring-double relative truncate rounded px-0.5 py-1 text-left text-on-surface-var',
        {
          'min-w-[60%]': breadcrumbCount === 2 && isLast,
          'min-w-[40%]': breadcrumbCount === 3 && isLast,
          'min-w-[30%]': breadcrumbCount === 4 && isLast,
          'hover:underline': breadcrumb.onClick && !isLast,
          'text-label-large': isLast,
          'cursor-pointer': breadcrumb.onClick && !isLast,
        }
      )}
      onClick={(e) => {
        e.preventDefault()
        if (!isLast) {
          breadcrumb.onClick?.()
        }
      }}
    >
      {breadcrumb.label}
    </a>
  )
}

export function Headline({
  breadcrumbs,
  children,
  className,
  tooltip,
  label,
  ...rest
}: React.PropsWithChildren<DOMAttributes<HTMLHeadingElement>> & {
  className?: string
  breadcrumbs?: HeadlineBreadcrumb[]
  tooltip?: string
  label?: string
}) {
  const { tt } = useTranslationTyped()
  const firstString = Children.toArray(children).filter(
    (child) => typeof child === 'string' && child !== '< '
  )[0] as string | undefined

  const ref = useRef<HTMLHeadingElement | null>(null)

  const { isTruncated } = useTruncationDetection({
    ref,
  })

  return (
    <div
      // negative margin is there because the buttons have top padding
      // for greater clickable area - we need to compensate for that
      className="-mt-1 flex min-w-0 flex-1 flex-col justify-between"
      data-test-id="headline"
    >
      {breadcrumbs && breadcrumbs.length > 0 && (
        <nav
          aria-label={tt.common.breadcrumbs()}
          className="mb-1 text-body-large"
        >
          <ul className="m-0 flex list-none flex-row items-center gap-1 p-0">
            {breadcrumbs.map((breadcrumb, index) => {
              const isLast = index === breadcrumbs.length - 1
              return (
                <React.Fragment key={index}>
                  <li className="flex min-w-0 items-center">
                    <BreadcrumbButton
                      breadcrumb={breadcrumb}
                      breadcrumbCount={breadcrumbs.length}
                      isLast={isLast}
                    />
                  </li>

                  {!isLast && (
                    <ChevronRight
                      size={12}
                      className="stroke-on-surface-var"
                      aria-hidden="true"
                    />
                  )}
                </React.Fragment>
              )
            })}
          </ul>
        </nav>
      )}
      {/* If label is set, use it, skip children, show them after h1 */}
      {/* If label is not set, use children in h1 */}
      {(label || children) && (
        <BreakoutTooltip
          placement="bottom"
          content={tooltip || label || ''}
          enabled={isTruncated}
          cloneRef={(clonedRef) => {
            // if the tooltip clones the h1, we need to re-point the ref to the cloned element
            ref.current = clonedRef as HTMLHeadingElement
          }}
        >
          <h1
            ref={ref}
            data-test-id="headline-title"
            className={
              className ||
              'cursor-default truncate text-display-medium antialiased'
            }
            aria-label={firstString || undefined}
            aria-live="polite"
            {...rest}
          >
            {label || children}
          </h1>
        </BreakoutTooltip>
      )}
      {/* If label is set, show children after h1 */}
      {Boolean(label) && children}
    </div>
  )
}

export function Actions(
  props: React.PropsWithChildren<
    DOMAttributes<HTMLDivElement> & { className?: string }
  >
) {
  return (
    <div
      className={classNames(
        'flex flex-row flex-wrap justify-end gap-4',
        props.className
      )}
    >
      {props.children}
    </div>
  )
}

export function Contents({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) {
  return (
    <div className={classNames('mt-[19.5px]', className)}> {children} </div>
  )
}

const variants = {
  enter: (mode: string) => {
    if (mode === 'slideIn') {
      return {
        opacity: 1,
        x: '200%',
      }
    }
    if (mode === 'slideOut') {
      return {
        opacity: 0,
        x: '0',
      }
    }
    return {
      x: '0',
      opacity: 0,
    }
  },
  animate: (mode: string) => {
    if (mode === 'slideIn') {
      return {
        opacity: 1,
        x: '0',
      }
    }
    if (mode === 'slideOut') {
      return {
        opacity: 1,
        x: '0',
      }
    }
    return {
      x: 0,
      opacity: 1,
    }
  },
  exit: (mode: string) => {
    if (mode === 'slideOut') {
      return {
        opacity: 1,
        x: '200%',
      }
    }
    return {
      opacity: 0,
    }
  },
}

export type AnimationController = {
  key: string | null
  mode: string
  component: JSX.Element | null
}

export type CalculateControllerFunction = (
  params: QueryParams,
  route: RouteType | undefined,
  currentController?: AnimationController
) => AnimationController

const transitionConfig = {
  // duration: 1,
  duration: 0.3,
}
export function TwoColumnAnimatedPane({
  calculateController,
}: {
  calculateController: CalculateControllerFunction
}) {
  const store = useRootStore()

  const initialController = useMemo(() => {
    const params = store.router.params
    const route = store.router.currentRoute
    return calculateController(params, route)
  }, [store, calculateController])

  const [controller, setController] =
    useState<AnimationController>(initialController)

  useEffect(() => {
    return reaction(
      () => ({ params: store.router.params, route: store.router.currentRoute }),
      ({ params, route }) => {
        const newController = calculateController(params, route, controller)
        if (controller.key === newController.key) return
        setController(newController)
      },
      { fireImmediately: true }
    )
  }, [store, controller, calculateController])

  return (
    <div className="relative h-full">
      <AnimatePresence mode="sync" initial={false} custom={controller.mode}>
        {controller.component && (
          <m.div
            variants={variants}
            custom={controller.mode}
            key={controller.key}
            className="absolute inset-0"
            initial="enter"
            animate="animate"
            exit="exit"
            transition={transitionConfig}
          >
            {controller.component}
          </m.div>
        )}
      </AnimatePresence>
    </div>
  )
}
